import { isDevHost } from '../config';
import type { TranscriptEntry } from '../flow/types';
// The contract comes from the C# controllers through Typewriter (api/public/supportChat, one client per
// endpoint controller). Type-only imports: their runtime (AppHttpProvider / PowerDuck) never reaches this
// bundle - the widget keeps its own small fetch transport below.
import type { SupportChatAvailabilityModel } from '../../api/public/supportChat/supportChatAvailabilityClient';
import type { SupportChatEscalationArgs, SupportChatEscalationModel, SupportChatTranscriptEntry } from '../../api/public/supportChat/supportChatEscalateClient';
import type { SupportChatInvoicingModel } from '../../api/public/supportChat/supportChatEventInvoicingClient';
import type { SupportChatLookupArgs, SupportChatLookupModel } from '../../api/public/supportChat/supportChatLookupClient';
import type { SupportChatMessageArgs, SupportChatMessagesArgs } from '../../api/public/supportChat/supportChatMessagesClient';
import type { SupportChatOrderStatusModel } from '../../api/public/supportChat/supportChatOrderStatusClient';
import type { SupportChatOrganizerContactModel } from '../../api/public/supportChat/supportChatOrganizerContactClient';
import type { SupportChatResendArgs, SupportChatResendModel } from '../../api/public/supportChat/supportChatResendClient';
import type { SupportChatEventArgs, SupportChatEventResolveModel } from '../../api/public/supportChat/supportChatResolveEventClient';
import type { SupportChatCodeSendModel, SupportChatEmailCodeArgs } from '../../api/public/supportChat/supportChatSendCodeClient';
import type { SupportChatStartArgs, SupportChatStartResult } from '../../api/public/supportChat/supportChatStartClient';
import type { SupportChatTrackEventArgs } from '../../api/public/supportChat/supportChatTrackClient';
import type { SupportChatCodeVerifyModel } from '../../api/public/supportChat/supportChatVerifyCodeClient';
import { SupportChatAvailabilityStatus } from '../../api/public/enums/supportChatAvailabilityStatus';
import { SupportChatCodeSendStatus } from '../../api/public/enums/supportChatCodeSendStatus';
import { SupportChatCodeVerifyStatus } from '../../api/public/enums/supportChatCodeVerifyStatus';
import { SupportChatEscalationStatus } from '../../api/public/enums/supportChatEscalationStatus';
import { SupportChatEventMatch } from '../../api/public/enums/supportChatEventMatch';
import { SupportChatEventStatus } from '../../api/public/enums/supportChatEventStatus';
import { SupportChatInvoicingStatus } from '../../api/public/enums/supportChatInvoicingStatus';
import { SupportChatLookupStatus } from '../../api/public/enums/supportChatLookupStatus';
import { SupportChatOrderSummaryStatus } from '../../api/public/enums/supportChatOrderSummaryStatus';
import { SupportChatResendStatus } from '../../api/public/enums/supportChatResendStatus';

/**
 * Backend contract of the support chat. Server side: one controller per endpoint in
 * Api/Version1/Webservice/SupportChat (SupportChatStartController, SupportChatLookupController, …),
 * the rules in Inviton.BL/SupportChat. Property names are PascalCase like every other Inviton API
 * (PropertyNamingPolicy = null). Responses are deliberately minimal: the browser only ever learns a
 * status (+ an opaque single-use token), never order objects.
 */

export type MessageWire = SupportChatMessageArgs;
export type MessagesRequest = SupportChatMessagesArgs;

/** Internal analytics event (camelCase); mapped to the wire shape in `track`. */
export interface TrackEvent {
	name: string;
	ts: number;
	conversationId: string | null;
	flowId?: string | null;
	stepId?: string | null;
	props?: Record<string, string | number | boolean | null>;
}

export interface ISupportChatApi {
	start(req: SupportChatStartArgs): Promise<SupportChatStartResult>;
	lookup(req: SupportChatLookupArgs): Promise<SupportChatLookupModel>;
	lookupFallback(req: SupportChatLookupArgs): Promise<SupportChatLookupModel>;
	/** Counts of completed orders / failed attempts under a code-verified address. */
	orderStatus(req: SupportChatLookupArgs): Promise<SupportChatOrderStatusModel>;
	/** Can a ticket for the event still be bought (public data, no verification). */
	availability(req: SupportChatEventArgs): Promise<SupportChatAvailabilityModel>;
	sendCode(req: SupportChatEmailCodeArgs): Promise<SupportChatCodeSendModel>;
	verifyCode(req: SupportChatEmailCodeArgs): Promise<SupportChatCodeVerifyModel>;
	resend(req: SupportChatResendArgs): Promise<SupportChatResendModel>;
	resolveEvent(req: SupportChatEventArgs): Promise<SupportChatEventResolveModel>;
	organizerContact(req: SupportChatEventArgs): Promise<SupportChatOrganizerContactModel>;
	eventInvoicing(req: SupportChatEventArgs): Promise<SupportChatInvoicingModel>;
	escalate(req: SupportChatEscalationArgs): Promise<SupportChatEscalationModel>;
	track(events: TrackEvent[]): Promise<void>;
	/** Conversation archive batch - fire and forget, like track. */
	messages(req: MessagesRequest): Promise<void>;
}

export function toTranscriptWire(transcript: TranscriptEntry[]): SupportChatTranscriptEntry[] {
	return transcript.map((m) => ({ Role: m.role, Text: m.text, StepId: m.stepId, FlowId: m.flowId, Ts: m.ts }));
}

export class SupportChatApiError extends Error {
	constructor(
		public readonly status: number,
		public readonly code: 'rate_limited' | 'conversation_expired' | 'bad_request' | 'server' | 'network' | 'timeout',
	) {
		super('SupportChatApiError:' + code);
	}
}

const REQUEST_TIMEOUT_MS = 20000;

export class HttpSupportChatApi implements ISupportChatApi {
	/** @param base API root, e.g. `/api/v1` - every endpoint is its own controller below it. */
	constructor(private readonly base: string) {}

	private async post<T>(controller: string, body: unknown): Promise<T> {
		const ctrl = new AbortController();
		const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
		let res: Response;
		try {
			res = await fetch(this.url(controller), {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify(body ?? {}),
				signal: ctrl.signal,
			});
		} catch (e) {
			clearTimeout(timer);
			throw new SupportChatApiError(0, (e as Error)?.name === 'AbortError' ? 'timeout' : 'network');
		}
		clearTimeout(timer);
		if (res.status === 429) {
			throw new SupportChatApiError(429, 'rate_limited');
		}
		// the server forgot the conversation (24 h max) → the widget starts a new one and retries
		if (res.status === 410) {
			throw new SupportChatApiError(410, 'conversation_expired');
		}
		if (res.status >= 400 && res.status < 500) {
			throw new SupportChatApiError(res.status, 'bad_request');
		}
		if (!res.ok) {
			throw new SupportChatApiError(res.status, 'server');
		}
		return (await res.json()) as T;
	}

	private url(controller: string) {
		return this.base.replace(/\/$/, '') + '/' + controller;
	}

	start(req: SupportChatStartArgs) {
		return this.post<SupportChatStartResult>('SupportChatStart', req);
	}
	lookup(req: SupportChatLookupArgs) {
		return this.post<SupportChatLookupModel>('SupportChatLookup', req);
	}
	lookupFallback(req: SupportChatLookupArgs) {
		return this.post<SupportChatLookupModel>('SupportChatLookupFallback', req);
	}
	orderStatus(req: SupportChatLookupArgs) {
		return this.post<SupportChatOrderStatusModel>('SupportChatOrderStatus', req);
	}
	availability(req: SupportChatEventArgs) {
		return this.post<SupportChatAvailabilityModel>('SupportChatAvailability', req);
	}
	sendCode(req: SupportChatEmailCodeArgs) {
		return this.post<SupportChatCodeSendModel>('SupportChatSendCode', req);
	}
	verifyCode(req: SupportChatEmailCodeArgs) {
		return this.post<SupportChatCodeVerifyModel>('SupportChatVerifyCode', req);
	}
	resend(req: SupportChatResendArgs) {
		return this.post<SupportChatResendModel>('SupportChatResend', req);
	}
	resolveEvent(req: SupportChatEventArgs) {
		return this.post<SupportChatEventResolveModel>('SupportChatResolveEvent', req);
	}
	organizerContact(req: SupportChatEventArgs) {
		return this.post<SupportChatOrganizerContactModel>('SupportChatOrganizerContact', req);
	}
	eventInvoicing(req: SupportChatEventArgs) {
		return this.post<SupportChatInvoicingModel>('SupportChatEventInvoicing', req);
	}
	escalate(req: SupportChatEscalationArgs) {
		return this.post<SupportChatEscalationModel>('SupportChatEscalate', req);
	}
	track(events: TrackEvent[]) {
		const wire: SupportChatTrackEventArgs[] = events.map((e) => ({
			Name: e.name,
			Ts: e.ts,
			ConversationId: e.conversationId,
			FlowId: e.flowId ?? null,
			StepId: e.stepId ?? null,
			PropsJson: e.props ? JSON.stringify(e.props) : null,
		}));
		return this.beacon('SupportChatTrack', { Events: wire });
	}
	messages(req: MessagesRequest) {
		return this.beacon('SupportChatMessages', req);
	}

	/** Fire-and-forget POST that survives page unload; never throws - a dropped batch is just a dropped batch. */
	private async beacon(controller: string, body: unknown) {
		try {
			const payload = JSON.stringify(body);
			if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
				navigator.sendBeacon(this.url(controller), new Blob([payload], { type: 'application/json' }));
				return;
			}
			await fetch(this.url(controller), {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'Content-Type': 'application/json' },
				body: payload,
				keepalive: true,
			});
		} catch {
			/* analytics must never break the widget */
		}
	}
}

/**
 * Deterministic in-browser mock for UI development and Playwright runs.
 * Enabled ONLY on dev hosts via `?sc-mock=1` (persisted in localStorage,
 * `?sc-mock=0` turns it off). The e-mail's local part selects the scenario:
 *   paid@…     → paid order, resend succeeds
 *   pending@…  → order found, not paid (card)
 *   transfer@… → order found, bank transfer on its way
 *   ended@…    → only an order for an event that already ended
 *   multi@…    → several orders (until an event is given), then paid order
 *   fail@…     → resend fails
 *   anything   → not found
 * Every address must first be verified with the code 123456 (any other code is invalid).
 */
export class MockSupportChatApi implements ISupportChatApi {
	private delay(ms = 700) {
		return new Promise<void>((r) => setTimeout(r, ms));
	}
	async start(): Promise<SupportChatStartResult> {
		await this.delay(200);
		return { ConversationId: 'mock-' + Math.random().toString(36).slice(2, 10) };
	}
	private verified = new Set<string>();
	async sendCode(req: SupportChatEmailCodeArgs): Promise<SupportChatCodeSendModel> {
		await this.delay(600);
		return { Status: req.OrderEmail.startsWith('nomail') ? SupportChatCodeSendStatus.Failed : SupportChatCodeSendStatus.Sent };
	}
	async verifyCode(req: SupportChatEmailCodeArgs): Promise<SupportChatCodeVerifyModel> {
		await this.delay(500);
		if (req.Code === '123456') {
			this.verified.add(req.OrderEmail.toLowerCase());
			return { Status: SupportChatCodeVerifyStatus.Verified };
		}
		return { Status: SupportChatCodeVerifyStatus.Invalid };
	}
	async lookup(req: SupportChatLookupArgs): Promise<SupportChatLookupModel> {
		await this.delay();
		if (!this.verified.has(req.OrderEmail.toLowerCase())) {
			return lookupModel(SupportChatLookupStatus.EmailNotVerified);
		}
		const local = req.OrderEmail.split('@')[0];
		const token = req.Purpose === 'resend' ? 'mock-token-' + local : null;
		if (local === 'multi' && req.EventId == null && !req.EventName) {
			return lookupModel(SupportChatLookupStatus.MultipleOrdersFound);
		}
		if (local === 'paid' || local === 'fail' || local === 'multi') {
			return { Status: SupportChatLookupStatus.PaidOrderFound, ActionToken: token, OrderCount: local === 'multi' ? 2 : 1 };
		}
		if (local === 'pending') {
			return lookupModel(SupportChatLookupStatus.OrderFoundNotPaid);
		}
		if (local === 'transfer') {
			return lookupModel(SupportChatLookupStatus.OrderFoundPendingTransfer);
		}
		if (local === 'ended') {
			return lookupModel(SupportChatLookupStatus.EventEnded);
		}
		return lookupModel(SupportChatLookupStatus.NotFound);
	}
	async lookupFallback(req: SupportChatLookupArgs): Promise<SupportChatLookupModel> {
		await this.delay(1100);
		if (!this.verified.has(req.OrderEmail.toLowerCase())) {
			return lookupModel(SupportChatLookupStatus.EmailNotVerified);
		}
		return lookupModel(SupportChatLookupStatus.NotFound);
	}
	/** Mock: the local part picks the scenario, same as `lookup` (nothing@ = no order at all). */
	async orderStatus(req: SupportChatLookupArgs): Promise<SupportChatOrderStatusModel> {
		await this.delay();
		if (!this.verified.has((req.OrderEmail ?? '').toLowerCase())) {
			return orderStatusModel(SupportChatOrderSummaryStatus.EmailNotVerified);
		}
		const local = (req.OrderEmail ?? '').split('@')[0];
		if (local === 'nothing') {
			return orderStatusModel(SupportChatOrderSummaryStatus.NothingFound);
		}
		if (local === 'transfer') {
			return orderStatusModel(SupportChatOrderSummaryStatus.PendingTransfer);
		}
		if (local === 'partial') {
			return orderStatusModel(SupportChatOrderSummaryStatus.NeedsManualCheck);
		}
		if (local === 'several') {
			// paid orders for two events: the event has to be named before anything is resent
			return req.EventId == null && !req.EventName
				? orderStatusModel(SupportChatOrderSummaryStatus.PaidSeveralEvents)
				: { Status: SupportChatOrderSummaryStatus.PaidOnly, ActionToken: 'mock-token-paid', ResendOrderCount: 1 };
		}
		if (local === 'paid') {
			return { Status: SupportChatOrderSummaryStatus.PaidOnly, ActionToken: 'mock-token-paid', ResendOrderCount: 1 };
		}
		if (local === 'pending' || local === 'multi') {
			return { Status: SupportChatOrderSummaryStatus.PaidAndFailedAttempts, ActionToken: 'mock-token-paid', ResendOrderCount: 1 };
		}
		return orderStatusModel(SupportChatOrderSummaryStatus.FailedAttemptsOnly);
	}
	/** Mock: the event name picks the scenario - "vypredane", "skoncil", "neskor", "mapa", "xyz". */
	async availability(req: SupportChatEventArgs): Promise<SupportChatAvailabilityModel> {
		await this.delay();
		const name = (req.EventName ?? '').toLowerCase();
		const base: SupportChatAvailabilityModel = {
			Status: SupportChatAvailabilityStatus.OnSale,
			EventName: req.EventName ?? 'QA Festival 2026',
			EventUrl: '/e-4242/qa-festival-2026',
			NextDate: '20. 10. 2026',
			SaleStart: null,
			SeatSelection: false,
			OtherDate: null,
		};
		if (req.EventId == null && (name.length < 3 || name.includes('xyz'))) {
			return { ...base, Status: SupportChatAvailabilityStatus.NotFound, EventUrl: null, NextDate: null };
		}
		if (name.includes('vypredan') || name.includes('sold')) {
			return { ...base, Status: SupportChatAvailabilityStatus.SoldOut, OtherDate: '27. 10. 2026' };
		}
		if (name.includes('skoncil') || name.includes('ended')) {
			return { ...base, Status: SupportChatAvailabilityStatus.SaleEnded };
		}
		if (name.includes('neskor') || name.includes('soon')) {
			return { ...base, Status: SupportChatAvailabilityStatus.SaleNotStarted, SaleStart: '18. 10. 2026' };
		}
		if (name.includes('bez predaja') || name.includes('no sale')) {
			return { ...base, Status: SupportChatAvailabilityStatus.NoOnlineSale };
		}
		if (name.includes('mapa') || name.includes('seat')) {
			return { ...base, SeatSelection: true };
		}
		return base;
	}
	async resend(req: SupportChatResendArgs): Promise<SupportChatResendModel> {
		await this.delay(900);
		return { Status: req.ActionToken.endsWith('fail') ? SupportChatResendStatus.Failed : SupportChatResendStatus.Sent };
	}
	/** Mock: names with a digit are exact matches, other names are fuzzy guesses, "xyz" is unknown. */
	async resolveEvent(req: SupportChatEventArgs): Promise<SupportChatEventResolveModel> {
		await this.delay();
		const name = req.EventName ?? '';
		if (req.EventId != null) {
			return { Status: SupportChatEventStatus.Found, EventId: req.EventId, EventName: name || 'Event z tejto stránky', EventDate: '1. 10. 2026', Match: SupportChatEventMatch.Exact };
		}
		if (name.length < 3 || name.toLowerCase().includes('xyz')) {
			return { Status: SupportChatEventStatus.NotFound, EventId: null, EventName: null, EventDate: null, Match: null };
		}
		const exact = /\d/.test(name);
		return {
			Status: SupportChatEventStatus.Found,
			EventId: 4242,
			EventName: exact ? name : name + ' 2026 – najväčší festival',
			EventDate: '1. 10. 2026',
			Match: exact ? SupportChatEventMatch.Exact : SupportChatEventMatch.Fuzzy,
		};
	}
	async organizerContact(req: SupportChatEventArgs): Promise<SupportChatOrganizerContactModel> {
		await this.delay();
		if (req.EventId != null || (req.EventName ?? '').length > 2) {
			// "bez kontaktu" in the name: an event whose organizer left no address
			if ((req.EventName ?? '').toLowerCase().includes('bez kontaktu')) {
				return { Status: SupportChatEventStatus.NotFound, OrganizerEmail: null, EventUrl: null };
			}
			return { Status: SupportChatEventStatus.Found, OrganizerEmail: 'organizator@example.com', EventUrl: '/e-4242/qa-festival-2026' };
		}
		return { Status: SupportChatEventStatus.NotFound, OrganizerEmail: null, EventUrl: null };
	}
	/** Mock: invoicing is enabled unless the event name contains "bez faktury". */
	async eventInvoicing(req: SupportChatEventArgs): Promise<SupportChatInvoicingModel> {
		await this.delay(500);
		const name = (req.EventName ?? '').toLowerCase();
		return { Status: name.includes('bez faktur') ? SupportChatInvoicingStatus.Disabled : SupportChatInvoicingStatus.Enabled };
	}
	async escalate(): Promise<SupportChatEscalationModel> {
		await this.delay(800);
		return { Status: SupportChatEscalationStatus.Created };
	}
	async messages(req: MessagesRequest) {
		if (typeof console !== 'undefined') {
			console.debug('[support-chat mock] messages', req.Messages.length, req.TruncateFrom);
		}
	}
	async track(events: TrackEvent[]) {
		if (typeof console !== 'undefined') {
			console.debug('[support-chat mock] track', events);
		}
	}
}

function orderStatusModel(status: SupportChatOrderSummaryStatus): SupportChatOrderStatusModel {
	return { Status: status, ActionToken: null, ResendOrderCount: 0 };
}

function lookupModel(status: SupportChatLookupStatus): SupportChatLookupModel {
	return { Status: status, ActionToken: null, OrderCount: 0 };
}

const MOCK_FLAG = 'inv-sc-mock';

export function createApi(apiBase: string): ISupportChatApi {
	if (isDevHost()) {
		try {
			const q = new URLSearchParams(location.search).get('sc-mock');
			if (q === '1') {
				localStorage.setItem(MOCK_FLAG, '1');
			}
			if (q === '0') {
				localStorage.removeItem(MOCK_FLAG);
			}
			if (localStorage.getItem(MOCK_FLAG) === '1') {
				return new MockSupportChatApi();
			}
		} catch {
			/* storage blocked */
		}
	}
	return new HttpSupportChatApi(apiBase);
}
